/*
 * Study paths: an ordered route through a subset of pages.
 *
 * The difference from siteNavigation's groups is the order. A group says these
 * pages belong together; a path says read them in this sequence, which means it
 * has a *next*. That is the whole point -- standing on the Wilderness Journey,
 * the next thing to study is the Levitical priesthood, not Joshua, because the
 * study continues there even though the canon does not.
 *
 * Deliberately not exhaustive. Every page belongs to a nav group, because the
 * nav has to reach everything; a path is a curated journey, so reference pages
 * (Psalms, Nations of the Bible, the Events explorer) sit outside them and are
 * found other ways. A guard checks every step points at a real page, not that
 * every page appears in some path.
 *
 * Named "paths" rather than "study guides" because the per-page how-to panel
 * already calls itself Study Guide on 46 pages, and two different things under
 * one name is worse than a slightly duller word.
 *
 * `section` ties a path to the header dropdown it should be offered from.
 *
 * `accent` is the path's own colour, stated rather than derived. It used to be
 * read from the nav group its first step belongs to, which broke the moment two
 * paths started in the same group: Taking the Land and The Kings both open in
 * Conquest & Kingdom, so both came out the same green and readers could not
 * tell whether they were looking at one path or two. Every value here is one
 * already in the nav palette -- no new colours, just a different assignment --
 * and a guard checks they stay distinct.
 */

export const STUDY_PATHS = [
  {
    id: "the-story-of-genesis",
    accent: "#2563eb", /* Beginnings */
    title: "The Story of Genesis",
    blurb:
      "Creation through the patriarchs, following the families the covenant passes through.",
    section: "old-testament",
    steps: [
      "/genesis",
      "/adam-noah",
      "/shem-lineage",
      "/abraham-lineage",
      "/jacob-lineage",
      "/esau-lineage",
    ],
  },
  {
    id: "the-wilderness-stage",
    accent: "#f59e0b", /* Exodus & the Law */
    title: "The Wilderness Stage",
    blurb:
      "Out of Egypt and into the desert years, and the priesthood formed along the way.",
    section: "old-testament",
    steps: [
      "/exodus",
      "/numbers",
      "/exodus-deuteronomy",
      "/levi-line",
      "/moses-aaron",
    ],
  },
  {
    id: "taking-the-land",
    accent: "#16a34a", /* Conquest & Kingdom */
    title: "Taking the Land",
    blurb: "Conquest under Joshua, and the long unsettled years that followed.",
    section: "old-testament",
    steps: ["/joshua", "/judges"],
  },
  {
    id: "the-kings",
    accent: "#0891b2", /* Wisdom & Poetry */
    title: "The Kings",
    blurb:
      "From one kingdom to two, traced through the lines of the men who ruled them.",
    section: "old-testament",
    steps: [
      "/1samuel-2chron",
      "/saul-lineage",
      "/david-lineage",
      "/kings-of-judah",
    ],
  },
  {
    id: "exile-and-return",
    accent: "#9d0191", /* Exile & Return */
    title: "Exile and Return",
    blurb:
      "The prophets who saw it coming, the exile itself, and the people who came home.",
    section: "old-testament",
    steps: [
      "/isaiah",
      "/jeremiah",
      "/ezekiel",
      "/minor-prophets",
      "/daniel",
      "/ezra-nehemiah-esther",
    ],
  },
  {
    id: "between-the-testaments",
    accent: "#ff7722", /* Between the Testaments */
    title: "Between the Testaments",
    blurb:
      "The four centuries the Bible does not narrate, and who held power through them.",
    section: "history",
    steps: [
      "/between-the-testaments",
      "/david-alexander",
      "/alexander-earlychurch",
      "/who-ruled-the-biblical-land",
      "/second-temple-leaders",
    ],
  },
  {
    id: "jesus-and-the-church",
    accent: "#1d4ed8", /* Jesus & the Church */
    title: "Jesus and the Church",
    blurb:
      "The gospels, the church that grew out of them, and the letters it was sent.",
    section: "new-testament",
    steps: [
      "/walk-through-the-gospels",
      "/church-history",
      "/early-church-history",
      "/church-letters",
      "/revelation",
    ],
  },
  {
    id: "how-we-got-the-bible",
    accent: "#0f766e", /* Scripture Itself */
    title: "How We Got the Bible",
    blurb:
      "How the text survived, and how the church came to recognise what belonged in it.",
    section: "history",
    steps: ["/scripture-preservation", "/scripture-formation-recognition"],
  },
];

/* The paths offered from one header dropdown. */
export function pathsForSection(sectionId) {
  return STUDY_PATHS.filter((path) => path.section === sectionId);
}

export function getStudyPath(pathId) {
  return STUDY_PATHS.find((path) => path.id === pathId) || null;
}

/*
 * Where a route sits in a path: its index, what comes next, and what came
 * before. Returns null when the route is not on the path at all, which is how
 * the on-page strip knows to stay hidden.
 */
export function locateInPath(pathId, route) {
  const path = getStudyPath(pathId);
  if (!path) return null;

  const index = path.steps.indexOf(route);
  if (index === -1) return null;

  return {
    path,
    index,
    step: index + 1,
    total: path.steps.length,
    previous: index > 0 ? path.steps[index - 1] : null,
    next: index < path.steps.length - 1 ? path.steps[index + 1] : null,
  };
}

export default STUDY_PATHS;
